/**
 * The hour panel while the campaign's hours are being read (user,
 * 12/10/2026: "a proper loading instead of a blank"): the panel's own shape
 * — the header with what is happening, the insight lines, the chart's named
 * lanes with 24 bars breathing in a wave, the hour labels — so nothing jumps
 * when the numbers land. Reduced motion: still.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { BarChart3, Loader2, Send, Sunrise, Users } from 'lucide-react';

import { Skeleton } from '@/components/ui';

import { ChartLane, HOUR_GRID } from './lanes/ChartLane';

/** A calm day's outline — the bars' resting heights (%), not data. */
const SHAPE = [18, 14, 34, 38, 62, 52, 78, 66, 40, 26, 28, 34, 38, 44, 30, 22, 12, 10, 12, 14, 18, 20, 16, 12];
const WAVE = 'animate-pulse motion-reduce:animate-none';

export const HourPanelSkeleton: FC = () => (
  <div className="flex flex-col gap-3" role="status" aria-live="polite">
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary motion-reduce:animate-none" aria-hidden />
      Reading this campaign&apos;s hours from the tracker…
    </div>
    <div className="flex flex-col gap-2.5 rounded-lg border border-border/60 bg-muted/20 px-3 py-3" aria-hidden>
      {[72, 88, 64].map((w, i) => (
        <div key={w} className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 shrink-0 rounded-full" style={{ animationDelay: `${i * 150}ms` }} />
          <Skeleton className="h-3" style={{ width: `${w}%`, animationDelay: `${i * 150}ms` }} />
        </div>
      ))}
    </div>
    <div className="flex flex-col gap-1" aria-hidden>
      <ChartLane icon={BarChart3} label="Score" className="h-28">
        <div className="grid h-full items-end gap-1" style={HOUR_GRID}>
          {SHAPE.map((h, i) => (
            <div key={i} className={`rounded-t-sm bg-muted ${WAVE}`} style={{ height: `${h}%`, animationDelay: `${i * 70}ms` }} />
          ))}
        </div>
      </ChartLane>
      <ChartLane icon={Sunrise} label="Window">
        <Skeleton className="h-4 w-2/3" />
      </ChartLane>
      <ChartLane icon={Users} label="Visits">
        <div className="grid h-3 gap-px" style={HOUR_GRID}>
          {SHAPE.map((_, i) => (
            <div key={i} className={`rounded-sm bg-muted ${WAVE}`} style={{ animationDelay: `${i * 70}ms` }} />
          ))}
        </div>
      </ChartLane>
      <ChartLane icon={Send} label="Drops" className="h-4 border-t border-border">
        <span />
      </ChartLane>
      <ChartLane>
        <div className="grid font-mono text-[10px] text-muted-foreground/60" style={HOUR_GRID}>
          {SHAPE.map((_, h) => (
            <span key={h} className="text-center">{String(h).padStart(2, '0')}</span>
          ))}
        </div>
      </ChartLane>
    </div>
    <Skeleton className="h-24 rounded-lg" aria-hidden />
  </div>
);
